import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Modal,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArrowBack,
  AutoFixHigh,
  Close,
  ContentCut,
  Speed,
  TextFields,
  VideoLibrary,
  Videocam,
  VolumeUp,
} from "@mui/icons-material";
import { addDoc, collection, doc, getDoc, serverTimestamp } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes, uploadBytesResumable } from "firebase/storage";
import { auth, db, storage } from "./firebase";
import { useTranslatedDictionary } from "./hooks/useTranslatedDictionary";
import {
  getReelFilterCss,
  REEL_FILTERS,
  REEL_SPEEDS,
  REEL_TEXT_COLORS,
  ReelOverlayText,
  resolveVideoDuration,
  useVideoAudioRefresh,
} from "./reelEdit";
import CameraCapture, { useShowCameraOptions } from "./CameraCapture";
import { PostDetailsFields, usePostDetails } from "./PostDetails";

const MAX_VIDEO_BYTES = 200 * 1024 * 1024; // Mismo límite que storage.rules
const MIN_TRIM_SECONDS = 1;
const MAX_OVERLAYS = 5;
const MAX_CAPTION = 2200;
const COVER_MAX_WIDTH = 720;

const TEXT = {
  es: {
    newReel: "Nuevo reel",
    pickHint: "Elige un video de tu galería o graba uno nuevo.",
    pickHintDesktop: "Elige un video de tu computadora.",
    chooseVideo: "Elegir video",
    recordVideo: "Grabar video",
    notVideo: "Ese archivo no es un video.",
    tooBig: "El video supera el máximo de 200 MB.",
    next: "Siguiente",
    toolTrim: "Recortar",
    toolSpeed: "Velocidad",
    toolFilters: "Filtros",
    toolText: "Texto",
    toolSound: "Sonido",
    trimRange: "Duración",
    trimUnavailable: "No se puede recortar este video porque no informa su duración.",
    filterNone: "Normal",
    filterVivid: "Vivo",
    filterWarm: "Cálido",
    filterCool: "Frío",
    filterBw: "B/N",
    filterRetro: "Retro",
    filterDrama: "Drama",
    textPlaceholder: "Escribe un texto...",
    addText: "Agregar",
    textHint: "Arrastra el texto sobre el video para ubicarlo.",
    originalSound: "Sonido original",
    details: "Detalles",
    cover: "Portada",
    coverHint: "Mueve la barra para elegir el cuadro de portada.",
    captionPlaceholder: "Escribe una descripción...",
    publish: "Compartir",
    publishing: "Publicando",
    publishError: "No se pudo publicar el reel. Intenta de nuevo.",
    discardTitle: "¿Descartar este reel?",
    discardWarning: "Se perderán los cambios que hiciste.",
    keepEditing: "Seguir editando",
    discard: "Descartar",
  },
  en: {
    newReel: "New reel",
    pickHint: "Choose a video from your gallery or record a new one.",
    pickHintDesktop: "Choose a video from your computer.",
    chooseVideo: "Choose video",
    recordVideo: "Record video",
    notVideo: "That file is not a video.",
    tooBig: "The video is over the 200 MB limit.",
    next: "Next",
    toolTrim: "Trim",
    toolSpeed: "Speed",
    toolFilters: "Filters",
    toolText: "Text",
    toolSound: "Sound",
    trimRange: "Length",
    trimUnavailable: "This video can't be trimmed because it doesn't report its length.",
    filterNone: "Normal",
    filterVivid: "Vivid",
    filterWarm: "Warm",
    filterCool: "Cool",
    filterBw: "B&W",
    filterRetro: "Retro",
    filterDrama: "Drama",
    textPlaceholder: "Type some text...",
    addText: "Add",
    textHint: "Drag the text on the video to place it.",
    originalSound: "Original sound",
    details: "Details",
    cover: "Cover",
    coverHint: "Move the bar to choose the cover frame.",
    captionPlaceholder: "Write a description...",
    publish: "Share",
    publishing: "Publishing",
    publishError: "Could not publish the reel. Please try again.",
    discardTitle: "Discard this reel?",
    discardWarning: "Your changes will be lost.",
    keepEditing: "Keep editing",
    discard: "Discard",
  },
};

const TOOLS = [
  { id: "trim", labelKey: "toolTrim", Icon: ContentCut },
  { id: "speed", labelKey: "toolSpeed", Icon: Speed },
  { id: "filters", labelKey: "toolFilters", Icon: AutoFixHigh },
  { id: "text", labelKey: "toolText", Icon: TextFields },
  { id: "sound", labelKey: "toolSound", Icon: VolumeUp },
];

const SLIDER_SX = { color: "#00d4ff" };
const CHIP_SX = (selected) => ({
  color: selected ? "#041015" : "#fff",
  bgcolor: selected ? "#00d4ff" : "rgba(255,255,255,0.1)",
  fontWeight: 600,
  "&:hover": { bgcolor: selected ? "#00d4ff" : "rgba(255,255,255,0.18)" },
});

const formatTime = (seconds) => {
  const total = Math.max(0, Math.round(seconds * 10) / 10);
  const minutes = Math.floor(total / 60);
  const rest = (total - minutes * 60).toFixed(1).padStart(4, "0");
  return `${minutes}:${rest}`;
};

const seekTo = (video, time) =>
  new Promise((resolve) => {
    if (Math.abs(video.currentTime - time) < 0.01) {
      resolve();
      return;
    }
    const timeout = setTimeout(resolve, 3000);
    video.addEventListener(
      "seeked",
      () => {
        clearTimeout(timeout);
        resolve();
      },
      { once: true },
    );
    video.currentTime = time;
  });

// La portada es opcional: si el navegador no puede generarla, el reel se publica sin ella.
const captureCover = async (video, time, filterCss) => {
  try {
    await seekTo(video, time);
    const scale = Math.min(1, COVER_MAX_WIDTH / video.videoWidth);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    const context = canvas.getContext("2d");
    context.filter = filterCss;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    return await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.85));
  } catch {
    return null;
  }
};

const PublicarReel = ({ onClose, onSuccess, people = [], language = "es" }) => {
  const t = useTranslatedDictionary(TEXT, language);
  const user = auth.currentUser;
  const showCameraOptions = useShowCameraOptions();
  const [step, setStep] = useState("pick");
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [duration, setDuration] = useState(0);
  const [trim, setTrim] = useState([0, 0]);
  const [speed, setSpeed] = useState(1);
  const [filter, setFilter] = useState("none");
  const [soundOff, setSoundOff] = useState(false);
  const [overlays, setOverlays] = useState([]);
  const [draftText, setDraftText] = useState("");
  const [textColor, setTextColor] = useState(REEL_TEXT_COLORS[0]);
  const [activeTool, setActiveTool] = useState("trim");
  const [coverTime, setCoverTime] = useState(0);
  const [caption, setCaption] = useState("");
  const details = usePostDetails({ user, people, language });
  const [userPhoto, setUserPhoto] = useState(user?.photoURL || "");
  const [publishing, setPublishing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);

  const videoRef = useRef(null);
  const stageRef = useRef(null);
  const galleryInputRef = useRef(null);
  const draggingIdRef = useRef(null);
  const draggingTrimRef = useRef(false);
  useVideoAudioRefresh(videoRef, previewUrl, soundOff);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = speed;
  }, [speed, previewUrl]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (step === "edit") {
      video.currentTime = trim[0];
      video.play().catch(() => {});
    } else if (step === "details") {
      video.pause();
      video.currentTime = coverTime;
    }
    // Solo reacciona al cambio de paso: recortar o mover la portada ya mueven el video.
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    e.target.value = "";
    if (selected) selectFile(selected);
  };

  const selectFile = (selected) => {
    if (!selected.type.startsWith("video/")) {
      setErrorMsg(t("notVideo"));
      return;
    }
    if (selected.size >= MAX_VIDEO_BYTES) {
      setErrorMsg(t("tooBig"));
      return;
    }
    setErrorMsg("");
    setFile(selected);
    setPreviewUrl(URL.createObjectURL(selected));
    setStep("edit");
  };

  const handleLoadedMetadata = async () => {
    const video = videoRef.current;
    if (!video) return;
    const length = await resolveVideoDuration(video);
    setDuration(length);
    setTrim([0, length]);
    video.playbackRate = speed;
    video.play().catch(() => {});
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video || step !== "edit" || !duration || draggingTrimRef.current) return;
    if (video.currentTime >= trim[1] || video.currentTime < trim[0]) {
      video.currentTime = trim[0];
    }
  };

  const handleTrimChange = (_, value, activeThumb) => {
    const [start, end] = value;
    if (end - start < MIN_TRIM_SECONDS) return;
    draggingTrimRef.current = true;
    videoRef.current?.pause();
    setTrim(value);
    if (videoRef.current) videoRef.current.currentTime = activeThumb === 0 ? start : end;
  };

  const handleTrimCommitted = () => {
    draggingTrimRef.current = false;
    if (videoRef.current) {
      videoRef.current.currentTime = trim[0];
      videoRef.current.play().catch(() => {});
    }
  };

  const goToDetails = () => {
    setCoverTime((current) => Math.min(trim[1], Math.max(trim[0], current)));
    setStep("details");
  };

  const handleCoverChange = (_, value) => {
    setCoverTime(value);
    if (videoRef.current) videoRef.current.currentTime = value;
  };

  const addOverlay = () => {
    const text = draftText.trim();
    if (!text || overlays.length >= MAX_OVERLAYS) return;
    setOverlays((prev) => [...prev, { id: `${Date.now()}`, text, x: 50, y: 40, color: textColor }]);
    setDraftText("");
  };

  const handleOverlayPointerDown = (e, id) => {
    if (step !== "edit") return;
    e.currentTarget.setPointerCapture(e.pointerId);
    draggingIdRef.current = id;
  };

  const handleOverlayPointerMove = (e) => {
    const id = draggingIdRef.current;
    const rect = stageRef.current?.getBoundingClientRect();
    if (!id || !rect) return;
    const x = Math.min(100, Math.max(0, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((e.clientY - rect.top) / rect.height) * 100));
    setOverlays((prev) => prev.map((overlay) => (overlay.id === id ? { ...overlay, x, y } : overlay)));
  };

  const handleOverlayPointerUp = () => {
    draggingIdRef.current = null;
  };

  const requestClose = () => {
    if (publishing) return;
    if (file) setConfirmDiscard(true);
    else onClose();
  };

  useEffect(() => {
    if (!user) return;
    getDoc(doc(db, "users", user.uid))
      .then((snap) => {
        if (snap.exists() && snap.data().photoURL) setUserPhoto(snap.data().photoURL);
      })
      .catch(() => {});
  }, [user]);

  const uploadVideo = () =>
    new Promise((resolve, reject) => {
      const task = uploadBytesResumable(
        ref(storage, `posts/${user.uid}/${Date.now()}_${file.name}`),
        file,
        { contentType: file.type },
      );
      task.on(
        "state_changed",
        (snapshot) => {
          if (snapshot.totalBytes) setProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
        },
        reject,
        () => getDownloadURL(task.snapshot.ref).then(resolve, reject),
      );
    });

  const uploadCover = async () => {
    const blob = videoRef.current ? await captureCover(videoRef.current, coverTime, getReelFilterCss(filter)) : null;
    if (!blob) return "";
    try {
      const coverRef = ref(storage, `posts/${user.uid}/${Date.now()}_cover.jpg`);
      await uploadBytes(coverRef, blob, { contentType: "image/jpeg" });
      return await getDownloadURL(coverRef);
    } catch {
      return "";
    }
  };

  const handlePublish = async () => {
    if (!user || !file || publishing) return;
    const detailsError = details.validate();
    if (detailsError) {
      setErrorMsg(detailsError);
      return;
    }
    setPublishing(true);
    setProgress(0);
    setErrorMsg("");
    try {
      let username = user.displayName || "";
      let userImg = user.photoURL || "";
      let handle = "";
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (snap.exists()) {
          const data = snap.data();
          username = data.displayName || username;
          userImg = data.photoURL || userImg;
          handle = data.username || "";
        }
      } catch {
        // Si falla la lectura se usan los datos de la sesión.
      }
      const emailName = user.email?.split("@")[0] || "usuario";

      await details.saveCloseFriends();

      const coverUrl = await uploadCover();
      const videoUrl = await uploadVideo();

      await addDoc(collection(db, "posts"), {
        uid: user.uid,
        username: username || emailName,
        handle: handle || emailName,
        userImg,
        text: details.withHashtags(caption),
        textBackground: "none",
        isReel: true,
        media: [{ type: "video", url: videoUrl, name: file.name }],
        imageUrl: "",
        videoUrl,
        coverUrl,
        ...details.fields(),
        reelEdit: {
          trimStart: duration ? trim[0] : 0,
          trimEnd: duration && trim[1] < duration - 0.05 ? trim[1] : null,
          speed,
          filter,
          muted: soundOff,
          overlays,
        },
        likes: [],
        savedBy: [],
        commentsCount: 0,
        timestamp: serverTimestamp(),
      });
      onSuccess();
    } catch (error) {
      console.error("Error al publicar el reel:", error);
      setErrorMsg(t("publishError"));
      setPublishing(false);
    }
  };

  const toolPanel = {
    trim: duration ? (
      <Box sx={{ px: 1.5 }}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="caption">{t("trimRange")}: {formatTime((trim[1] - trim[0]) / speed)}</Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)" }}>
            {formatTime(trim[0])} - {formatTime(trim[1])}
          </Typography>
        </Stack>
        <Slider
          value={trim}
          min={0}
          max={duration}
          step={0.1}
          disableSwap
          onChange={handleTrimChange}
          onChangeCommitted={handleTrimCommitted}
          sx={SLIDER_SX}
        />
      </Box>
    ) : (
      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", px: 1 }}>{t("trimUnavailable")}</Typography>
    ),
    speed: (
      <Stack direction="row" spacing={1} justifyContent="center">
        {REEL_SPEEDS.map((value) => (
          <Chip key={value} label={`${value}x`} onClick={() => setSpeed(value)} sx={CHIP_SX(speed === value)} />
        ))}
      </Stack>
    ),
    filters: (
      <Stack direction="row" spacing={1} sx={{ overflowX: "auto", pb: 0.5, scrollbarWidth: "none" }}>
        {REEL_FILTERS.map((option) => (
          <Chip key={option.id} label={t(option.labelKey)} onClick={() => setFilter(option.id)} sx={{ ...CHIP_SX(filter === option.id), flexShrink: 0 }} />
        ))}
      </Stack>
    ),
    text: (
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            fullWidth
            size="small"
            variant="standard"
            placeholder={t("textPlaceholder")}
            value={draftText}
            onChange={(e) => setDraftText(e.target.value.slice(0, 100))}
            InputProps={{ disableUnderline: true, sx: { color: "#fff" } }}
          />
          <Button
            onClick={addOverlay}
            disabled={!draftText.trim() || overlays.length >= MAX_OVERLAYS}
            sx={{ color: "#00d4ff", textTransform: "none", fontWeight: 700 }}
          >
            {t("addText")}
          </Button>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {REEL_TEXT_COLORS.map((color) => (
            <Box
              key={color}
              component="button"
              type="button"
              aria-label={color}
              onClick={() => setTextColor(color)}
              sx={{
                width: 24,
                height: 24,
                p: 0,
                borderRadius: "50%",
                cursor: "pointer",
                bgcolor: color,
                border: textColor === color ? "2px solid #00d4ff" : "2px solid rgba(255,255,255,0.35)",
              }}
            />
          ))}
        </Stack>
        {overlays.length > 0 && (
          <>
            <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.75 }}>
              {overlays.map((overlay) => (
                <Chip
                  key={overlay.id}
                  size="small"
                  label={overlay.text}
                  onDelete={() => setOverlays((prev) => prev.filter((item) => item.id !== overlay.id))}
                  sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.1)", maxWidth: 180, "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.7)" } }}
                />
              ))}
            </Stack>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)" }}>{t("textHint")}</Typography>
          </>
        )}
      </Stack>
    ),
    sound: (
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1 }}>
        <Typography variant="body2">{t("originalSound")}</Typography>
        <Switch
          checked={!soundOff}
          onChange={(e) => setSoundOff(!e.target.checked)}
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": { color: "#00d4ff" },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#00d4ff" },
          }}
        />
      </Stack>
    ),
  };

  return (
    <Modal open onClose={requestClose} hideBackdrop>
      <Box sx={{ position: "fixed", inset: 0, display: "flex", flexDirection: "column", bgcolor: "#000", color: "#fff", outline: "none" }}>
        {/* Encabezado */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1, py: 0.5, flexShrink: 0 }}>
          {step === "details" ? (
            <IconButton disabled={publishing} onClick={() => setStep("edit")} sx={{ color: "#fff" }}>
              <ArrowBack />
            </IconButton>
          ) : (
            <IconButton onClick={requestClose} sx={{ color: "#fff" }}>
              <Close />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {step === "details" ? t("details") : t("newReel")}
          </Typography>
          {step === "edit" ? (
            <Button onClick={goToDetails} sx={{ color: "#00d4ff", textTransform: "none", fontWeight: 700 }}>
              {t("next")}
            </Button>
          ) : (
            <Box sx={{ width: 40 }} />
          )}
        </Stack>

        {errorMsg && (
          <Alert severity="error" variant="filled" onClose={() => setErrorMsg("")} sx={{ mx: 1, mb: 1, borderRadius: "12px", flexShrink: 0 }}>
            {errorMsg}
          </Alert>
        )}

        {step === "pick" && (
          <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ flex: 1, px: 3, textAlign: "center" }}>
            <Typography sx={{ color: "rgba(255,255,255,0.75)" }}>{t(showCameraOptions ? "pickHint" : "pickHintDesktop")}</Typography>
            <Button
              variant="contained"
              startIcon={<VideoLibrary />}
              onClick={() => galleryInputRef.current?.click()}
              sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 700, width: 240, background: "linear-gradient(45deg, #00d4ff 30%, #0045ff 90%)" }}
            >
              {t("chooseVideo")}
            </Button>
            {showCameraOptions && (
              <Button
                variant="outlined"
                startIcon={<Videocam />}
                onClick={() => setCameraOpen(true)}
                sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 700, width: 240, color: "#00d4ff", borderColor: "#00d4ff" }}
              >
                {t("recordVideo")}
              </Button>
            )}
          </Stack>
        )}

        <input ref={galleryInputRef} type="file" hidden accept="video/*" onChange={handleFileChange} />
        {cameraOpen && (
          <CameraCapture
            mode="video"
            language={language}
            onClose={() => setCameraOpen(false)}
            onCapture={(file) => {
              setCameraOpen(false);
              selectFile(file);
            }}
          />
        )}

        {previewUrl && (
          <Box ref={stageRef} sx={{ position: "relative", flex: 1, minHeight: 0, width: "100%", maxWidth: 480, mx: "auto", overflow: "hidden" }}>
            <Box
              component="video"
              ref={videoRef}
              src={previewUrl}
              playsInline
              muted={soundOff}
              onLoadedMetadata={handleLoadedMetadata}
              onTimeUpdate={handleTimeUpdate}
              onEnded={() => {
                if (step !== "edit" || !videoRef.current) return;
                videoRef.current.currentTime = trim[0];
                videoRef.current.play().catch(() => {});
              }}
              sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", filter: getReelFilterCss(filter) }}
            />
            {overlays.map((overlay) => (
              <ReelOverlayText
                key={overlay.id}
                overlay={overlay}
                onPointerDown={(e) => handleOverlayPointerDown(e, overlay.id)}
                onPointerMove={handleOverlayPointerMove}
                onPointerUp={handleOverlayPointerUp}
                onPointerCancel={handleOverlayPointerUp}
                sx={{ touchAction: "none", cursor: step === "edit" ? "grab" : "default", userSelect: "none" }}
              />
            ))}
          </Box>
        )}

        {step === "edit" && (
          <Box sx={{ flexShrink: 0, width: "100%", maxWidth: 480, mx: "auto", boxSizing: "border-box", p: 1 }}>
            <Box sx={{ minHeight: 64, py: 1 }}>{toolPanel[activeTool]}</Box>
            <Stack direction="row" justifyContent="space-around">
              {TOOLS.map(({ id, labelKey, Icon }) => (
                <Button
                  key={id}
                  onClick={() => setActiveTool(id)}
                  sx={{
                    flexDirection: "column",
                    minWidth: 0,
                    flex: 1,
                    gap: 0.25,
                    textTransform: "none",
                    fontSize: "0.72rem",
                    color: activeTool === id ? "#00d4ff" : "#fff",
                  }}
                >
                  <Icon fontSize="small" />
                  {t(labelKey)}
                </Button>
              ))}
            </Stack>
          </Box>
        )}

        {step === "details" && (
          <Box sx={{ flexShrink: 0, width: "100%", maxWidth: 480, maxHeight: "62%", overflowY: "auto", mx: "auto", boxSizing: "border-box", p: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{t("cover")}</Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)" }}>{t("coverHint")}</Typography>
            {duration > 0 && (
              <Slider
                value={coverTime}
                min={trim[0]}
                max={trim[1]}
                step={0.1}
                disabled={publishing}
                onChange={handleCoverChange}
                sx={SLIDER_SX}
              />
            )}
            <Stack direction="row" spacing={1.5} sx={{ my: 1 }}>
              <Avatar src={userPhoto} sx={{ width: 34, height: 34 }} />
              <TextField
                fullWidth
                multiline
                maxRows={3}
                variant="standard"
                placeholder={t("captionPlaceholder")}
                value={caption}
                disabled={publishing}
                onChange={(e) => setCaption(e.target.value.slice(0, MAX_CAPTION))}
                InputProps={{ disableUnderline: true, sx: { color: "#fff" } }}
              />
            </Stack>
            <PostDetailsFields details={details} disabled={publishing} />

            {publishing && (
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{ mb: 1, height: 6, borderRadius: "999px", bgcolor: "rgba(255,255,255,0.1)", "& .MuiLinearProgress-bar": { bgcolor: "#00d4ff" } }}
              />
            )}
            <Button
              fullWidth
              variant="contained"
              disabled={publishing}
              onClick={handlePublish}
              sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 700, background: "linear-gradient(45deg, #00d4ff 30%, #0045ff 90%)" }}
            >
              {publishing ? `${t("publishing")} ${progress}%` : t("publish")}
            </Button>
          </Box>
        )}

        <Dialog
          open={confirmDiscard}
          onClose={() => setConfirmDiscard(false)}
          fullWidth
          maxWidth="xs"
          PaperProps={{ sx: { bgcolor: "#141414", backgroundImage: "none", color: "#fff", borderRadius: "18px" } }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>{t("discardTitle")}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)" }}>{t("discardWarning")}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDiscard(false)} sx={{ color: "#fff", textTransform: "none" }}>
              {t("keepEditing")}
            </Button>
            <Button onClick={onClose} sx={{ color: "#ff6b6b", textTransform: "none", fontWeight: 700 }}>
              {t("discard")}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Modal>
  );
};

export default PublicarReel;
